import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Webhook,
  Activity,
  CheckCircle2,
  XCircle,
  Clock,
  RefreshCw,
  Plus,
  Trash2,
  Play,
  Pause,
  ExternalLink,
  Copy,
  AlertTriangle,
  Send,
  Eye,
  Settings,
  Zap,
  TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';

// Status badge component
const StatusBadge = ({ success }) => (
  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
    success 
      ? 'bg-neon-green/20 text-neon-green' 
      : 'bg-red-500/20 text-red-500'
  }`}>
    {success ? 'Success' : 'Failed'}
  </span>
);

// Stat card component
const StatCard = ({ icon: Icon, label, value, subValue, color = 'neon-cyan' }) => (
  <div className="glass-card p-4">
    <div className="flex items-start justify-between">
      <div className={`w-10 h-10 rounded-lg bg-${color}/20 flex items-center justify-center`}>
        <Icon className={`w-5 h-5 text-${color}`} />
      </div>
    </div>
    <div className="mt-3">
      <div className="text-2xl font-bold text-white">{value}</div>
      <div className="text-sm text-slate-400">{label}</div>
      {subValue && <div className="text-xs text-slate-500 mt-1">{subValue}</div>}
    </div>
  </div>
);

// Delivery row component
const DeliveryRow = ({ delivery, onViewDetails }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    className="flex items-center justify-between p-3 bg-white/5 rounded-lg hover:bg-white/8 transition-colors cursor-pointer"
    onClick={() => onViewDetails(delivery)}
  >
    <div className="flex items-center space-x-3">
      {delivery.success ? (
        <CheckCircle2 className="w-5 h-5 text-neon-green" />
      ) : (
        <XCircle className="w-5 h-5 text-red-500" />
      )}
      <div>
        <div className="text-white text-sm font-medium">{delivery.event_type}</div>
        <div className="text-xs text-slate-400">
          {new Date(delivery.delivered_at).toLocaleString()}
        </div>
      </div>
    </div>
    <div className="flex items-center space-x-4">
      <div className="text-right">
        <div className="text-sm text-slate-300">
          {delivery.status_code || 'N/A'}
        </div>
        <div className="text-xs text-slate-500">
          {delivery.duration_ms}ms
        </div>
      </div>
      <StatusBadge success={delivery.success} />
    </div>
  </motion.div>
);

// Webhook config card
const WebhookCard = ({ webhook, onToggle, onDelete, onTest }) => {
  const [testing, setTesting] = useState(false);
  
  const handleTest = async () => {
    setTesting(true);
    await onTest(webhook);
    setTesting(false);
  };
  
  return (
    <div className={`glass-card p-4 ${!webhook.is_active ? 'opacity-60' : ''}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center space-x-3">
          <div className={`w-10 h-10 rounded-lg ${webhook.is_active ? 'bg-neon-green/20' : 'bg-slate-700'} flex items-center justify-center`}>
            <Webhook className={`w-5 h-5 ${webhook.is_active ? 'text-neon-green' : 'text-slate-400'}`} />
          </div>
          <div>
            <div className="text-white font-medium">{webhook.name}</div>
            <div className="text-xs text-slate-400 truncate max-w-[200px]">{webhook.url}</div>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => onToggle(webhook.id)}
            className={`p-2 rounded-lg transition-colors ${
              webhook.is_active 
                ? 'bg-neon-green/20 text-neon-green hover:bg-neon-green/30' 
                : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
            }`}
          >
            {webhook.is_active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </button>
          <button
            onClick={() => onDelete(webhook.id)}
            className="p-2 rounded-lg bg-red-500/20 text-red-500 hover:bg-red-500/30 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
      
      <div className="flex flex-wrap gap-1 mb-3">
        {webhook.events?.slice(0, 3).map((event, idx) => (
          <span key={idx} className="px-2 py-0.5 bg-slate-700 rounded text-xs text-slate-300">
            {event.replace('compliance.', '')}
          </span>
        ))}
        {webhook.events?.length > 3 && (
          <span className="px-2 py-0.5 bg-slate-700 rounded text-xs text-slate-400">
            +{webhook.events.length - 3}
          </span>
        )}
      </div>
      
      <div className="flex items-center justify-between pt-3 border-t border-white/10">
        <div className="text-xs text-slate-400">
          {webhook.failure_count > 0 ? (
            <span className="text-red-400">{webhook.failure_count} failures</span>
          ) : (
            <span className="text-neon-green">Healthy</span>
          )}
        </div>
        <button
          onClick={handleTest}
          disabled={testing}
          className="text-xs text-neon-cyan hover:text-neon-cyan/80 flex items-center"
        >
          {testing ? (
            <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
          ) : (
            <Send className="w-3 h-3 mr-1" />
          )}
          Test
        </button>
      </div>
    </div>
  );
};

// Add webhook modal
const AddWebhookModal = ({ isOpen, onClose, onAdd }) => {
  const [name, setName] = useState('OVA Dispatch');
  const [url, setUrl] = useState('');
  const [secret, setSecret] = useState('');
  const [events, setEvents] = useState(['compliance.lockout', 'compliance.unlock', 'compliance.score_change']);
  const [loading, setLoading] = useState(false);
  
  const allEvents = [
    'compliance.lockout',
    'compliance.unlock',
    'compliance.score_change',
    'compliance.credential_expired',
    'compliance.alert'
  ];
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    await onAdd({ name, url, secret, events });
    setLoading(false);
    onClose();
  };
  
  if (!isOpen) return null;
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="glass-card p-6 max-w-md w-full"
        onClick={e => e.stopPropagation()}
      >
        <h3 className="text-xl font-bold text-white mb-4 flex items-center">
          <Plus className="w-5 h-5 mr-2 text-neon-cyan" />
          Register Webhook
        </h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm text-slate-400 mb-1 block">Name</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white"
              placeholder="OVA Dispatch"
              required
            />
          </div>
          
          <div>
            <label className="text-sm text-slate-400 mb-1 block">Webhook URL</label>
            <input
              type="url"
              value={url}
              onChange={e => setUrl(e.target.value)}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white"
              placeholder="https://api.ovadispatch.com/webhooks/compliance"
              required
            />
          </div>
          
          <div>
            <label className="text-sm text-slate-400 mb-1 block">Secret Key</label>
            <input
              type="password"
              value={secret}
              onChange={e => setSecret(e.target.value)}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white"
              placeholder="whsec_..."
              required
            />
          </div>
          
          <div>
            <label className="text-sm text-slate-400 mb-2 block">Events</label>
            <div className="space-y-2">
              {allEvents.map(event => (
                <label key={event} className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={events.includes(event)}
                    onChange={e => {
                      if (e.target.checked) {
                        setEvents([...events, event]);
                      } else {
                        setEvents(events.filter(ev => ev !== event));
                      }
                    }}
                    className="rounded bg-slate-700 border-slate-600 text-neon-cyan focus:ring-neon-cyan"
                  />
                  <span className="text-sm text-slate-300">{event}</span>
                </label>
              ))}
            </div>
          </div>
          
          <div className="flex space-x-3 pt-4">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="btn-primary flex-1 flex items-center justify-center">
              {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Register'}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

// Delivery detail modal
const DeliveryDetailModal = ({ delivery, isOpen, onClose }) => {
  if (!isOpen || !delivery) return null;
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="glass-card p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-xl font-bold text-white flex items-center">
              <Eye className="w-5 h-5 mr-2 text-neon-cyan" />
              Delivery Details
            </h3>
            <p className="text-sm text-slate-400 mt-1">{delivery.event_id}</p>
          </div>
          <StatusBadge success={delivery.success} />
        </div>
        
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 bg-white/5 rounded-lg">
              <div className="text-xs text-slate-400 mb-1">Event Type</div>
              <div className="text-white">{delivery.event_type}</div>
            </div>
            <div className="p-3 bg-white/5 rounded-lg">
              <div className="text-xs text-slate-400 mb-1">Status Code</div>
              <div className="text-white">{delivery.status_code || 'N/A'}</div>
            </div>
            <div className="p-3 bg-white/5 rounded-lg">
              <div className="text-xs text-slate-400 mb-1">Duration</div>
              <div className="text-white">{delivery.duration_ms}ms</div>
            </div>
            <div className="p-3 bg-white/5 rounded-lg">
              <div className="text-xs text-slate-400 mb-1">Timestamp</div>
              <div className="text-white text-sm">{new Date(delivery.delivered_at).toLocaleString()}</div>
            </div>
          </div>
          
          <div>
            <div className="text-sm text-slate-400 mb-2">URL</div>
            <div className="p-3 bg-slate-800 rounded-lg font-mono text-xs text-neon-cyan break-all">
              {delivery.url}
            </div>
          </div>
          
          <div>
            <div className="text-sm text-slate-400 mb-2">Payload</div>
            <pre className="p-3 bg-slate-800 rounded-lg font-mono text-xs text-slate-300 overflow-x-auto">
              {JSON.stringify(delivery.payload, null, 2)}
            </pre>
          </div>
          
          {delivery.error && (
            <div>
              <div className="text-sm text-red-400 mb-2">Error</div>
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                {delivery.error}
              </div>
            </div>
          )}
          
          {delivery.response_body && (
            <div>
              <div className="text-sm text-slate-400 mb-2">Response</div>
              <pre className="p-3 bg-slate-800 rounded-lg font-mono text-xs text-slate-300 overflow-x-auto">
                {delivery.response_body}
              </pre>
            </div>
          )}
        </div>
        
        <div className="mt-6 flex justify-end">
          <button onClick={onClose} className="btn-secondary">Close</button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export const WebhookDashboard = () => {
  const [webhooks, setWebhooks] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    success: 0,
    failed: 0,
    avgDuration: 0
  });
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  
  const fetchData = async () => {
    try {
      const [webhooksRes, deliveriesRes] = await Promise.all([
        fetch(`${API_URL}/api/xchange/webhooks/configs`),
        fetch(`${API_URL}/api/xchange/webhooks/deliveries?limit=50`)
      ]);
      
      const webhooksData = await webhooksRes.json();
      const deliveriesData = await deliveriesRes.json();
      
      setWebhooks(webhooksData.webhooks || []);
      setDeliveries(deliveriesData.deliveries || []);
      
      // Calculate stats
      const allDeliveries = deliveriesData.deliveries || [];
      const successCount = allDeliveries.filter(d => d.success).length;
      const avgDuration = allDeliveries.length > 0
        ? Math.round(allDeliveries.reduce((sum, d) => sum + (d.duration_ms || 0), 0) / allDeliveries.length)
        : 0;
      
      setStats({
        total: allDeliveries.length,
        success: successCount,
        failed: allDeliveries.length - successCount,
        avgDuration
      });
    } catch (error) {
      console.error('Failed to fetch webhook data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  
  useEffect(() => {
    fetchData();
    
    // Auto-refresh every 10 seconds
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, []);
  
  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };
  
  const handleAddWebhook = async (config) => {
    try {
      const response = await fetch(`${API_URL}/api/xchange/webhooks/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config)
      });
      
      if (response.ok) {
        toast.success('Webhook registered successfully!');
        fetchData();
      } else {
        toast.error('Failed to register webhook');
      }
    } catch (error) {
      console.error('Failed to add webhook:', error);
      toast.error('Failed to register webhook');
    }
  };
  
  const handleToggleWebhook = async (webhookId) => {
    try {
      const response = await fetch(`${API_URL}/api/xchange/webhooks/configs/${webhookId}/toggle`, {
        method: 'POST'
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchData();
      }
    } catch (error) {
      console.error('Failed to toggle webhook:', error);
    }
  };
  
  const handleDeleteWebhook = async (webhookId) => {
    if (!window.confirm('Are you sure you want to delete this webhook?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/xchange/webhooks/configs/${webhookId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        toast.success('Webhook deleted');
        fetchData();
      }
    } catch (error) {
      console.error('Failed to delete webhook:', error);
    }
  };
  
  const handleTestWebhook = async (webhook) => {
    try {
      const response = await fetch(`${API_URL}/api/xchange/webhooks/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: webhook.url, secret: 'test' })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Webhook test successful!');
      } else {
        toast.error(`Test failed: ${data.error || 'Unknown error'}`);
      }
      
      fetchData();
    } catch (error) {
      console.error('Failed to test webhook:', error);
      toast.error('Test failed');
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-neon-cyan animate-spin" />
      </div>
    );
  }
  
  const successRate = stats.total > 0 ? Math.round((stats.success / stats.total) * 100) : 100;
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-white flex items-center">
            <Webhook className="w-6 h-6 mr-2 text-neon-cyan" />
            Webhook Monitor
          </h1>
          <p className="text-slate-400 text-sm mt-1">Real-time dispatch integration monitoring</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="btn-secondary flex items-center"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="btn-primary flex items-center"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Webhook
          </button>
        </div>
      </div>
      
      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Activity}
          label="Total Deliveries"
          value={stats.total}
          color="neon-cyan"
        />
        <StatCard
          icon={CheckCircle2}
          label="Successful"
          value={stats.success}
          subValue={`${successRate}% success rate`}
          color="neon-green"
        />
        <StatCard
          icon={XCircle}
          label="Failed"
          value={stats.failed}
          color="red-500"
        />
        <StatCard
          icon={Zap}
          label="Avg. Response"
          value={`${stats.avgDuration}ms`}
          color="neon-purple"
        />
      </div>
      
      {/* Main content */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Webhooks */}
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-white flex items-center">
            <Settings className="w-5 h-5 mr-2 text-slate-400" />
            Registered Webhooks
          </h2>
          
          {webhooks.length > 0 ? (
            webhooks.map(webhook => (
              <WebhookCard
                key={webhook.id}
                webhook={webhook}
                onToggle={handleToggleWebhook}
                onDelete={handleDeleteWebhook}
                onTest={handleTestWebhook}
              />
            ))
          ) : (
            <div className="glass-card p-8 text-center">
              <Webhook className="w-12 h-12 mx-auto mb-4 text-slate-600" />
              <p className="text-slate-400">No webhooks registered</p>
              <button
                onClick={() => setShowAddModal(true)}
                className="mt-4 text-neon-cyan hover:text-neon-cyan/80"
              >
                Add your first webhook →
              </button>
            </div>
          )}
        </div>
        
        {/* Delivery history */}
        <div className="lg:col-span-2 glass-card p-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center justify-between">
            <span className="flex items-center">
              <TrendingUp className="w-5 h-5 mr-2 text-neon-green" />
              Recent Deliveries
            </span>
            <span className="text-sm text-slate-400">{deliveries.length} events</span>
          </h2>
          
          <div className="space-y-2 max-h-[500px] overflow-y-auto pr-2">
            {deliveries.length > 0 ? (
              deliveries.map((delivery, idx) => (
                <DeliveryRow
                  key={idx}
                  delivery={delivery}
                  onViewDetails={setSelectedDelivery}
                />
              ))
            ) : (
              <div className="text-center py-12 text-slate-400">
                <Activity className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No webhook deliveries yet</p>
                <p className="text-sm">Events will appear here when webhooks are triggered</p>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Modals */}
      <AnimatePresence>
        {showAddModal && (
          <AddWebhookModal
            isOpen={showAddModal}
            onClose={() => setShowAddModal(false)}
            onAdd={handleAddWebhook}
          />
        )}
      </AnimatePresence>
      
      <AnimatePresence>
        {selectedDelivery && (
          <DeliveryDetailModal
            delivery={selectedDelivery}
            isOpen={!!selectedDelivery}
            onClose={() => setSelectedDelivery(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default WebhookDashboard;
